import { Card, Divider, Space, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowDownRight, TbArrowUpRight, TbMinus, TbSquareRounded } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { MetricChartSeries } from '@/shared/components/data-visualization/recharts';
import FinancialCompanyAvatar from '@/shared/components/financial-statements/components/FinancialCompanyAvatar';
import { FinancialMetricValue } from '@/shared/components/financial-values';
import { TickerExchangeTag } from '@/shared/components/tags';
import { dayjs, formatFinancialPeriodLabel, FundamentalValuationsType } from '@/utilities';

type TooltipDataRow = Record<string, unknown> & { date?: string };

interface TooltipPayloadItem {
    dataKey?: unknown;
    name?: number | string;
    payload?: TooltipDataRow;
    value?: number | string | readonly (number | string)[];
}

interface RangeSummaryItem {
    changePercent: number | null;
    lineItem: string;
}

interface CustomRechartsTooltipProps {
    active?: boolean;
    data?: TooltipDataRow[];
    lineItemLabels?: Partial<Record<string, string>>;
    payload?: readonly TooltipPayloadItem[];
    rangeSummary?: {
        endLabel: string;
        items: RangeSummaryItem[];
        startLabel: string;
    };
    segmentedValue?: string;
    series?: MetricChartSeries[];
    type?: FundamentalValuationsType;
    typeByLineItem?: Partial<Record<string, FundamentalValuationsType>>;
}

const numericValue = (value: unknown) => {
    const result = Number(value);

    return Number.isFinite(result) ? result : null;
};

const changePercent = (currentValue: unknown, previousValue: unknown) => {
    const current = numericValue(currentValue);
    const previous = numericValue(previousValue);

    if (current === null || previous === null || previous === 0) {
        return null;
    }

    return ((current - previous) / Math.abs(previous)) * 100;
};

const formatPeriodLabel = (row: TooltipDataRow | undefined, segmentedValue?: string) => {
    if (!row) {
        return '';
    }

    if (segmentedValue === 'annual') {
        return String(row.calendarYear ?? row.fiscalYear ?? dayjs(row.date).year());
    }

    if (row.periodLabel) {
        return formatFinancialPeriodLabel(String(row.periodLabel));
    }

    const date = dayjs(row.date);

    return segmentedValue === 'quarterly'
        ? `Q${date.quarter()} ${date.year()}`
        : formatFinancialPeriodLabel(String(row.date ?? ''));
};

const ChangeValue = ({ change, suffix }: { change: number | null; suffix: string }) => {
    const { token } = useAntDesignTheme();
    const ChangeIcon = change === null || change === 0 ? TbMinus : change > 0 ? TbArrowUpRight : TbArrowDownRight;
    const iconColor =
        change === null || change === 0 ? token.colorTextTertiary : change > 0 ? token.green4 : token.red4;

    return (
        <span
            style={{
                alignItems: 'center',
                color: token.colorTextTertiary,
                display: 'inline-flex',
                gap: '0.125rem',
            }}
        >
            <ChangeIcon aria-hidden color={iconColor} size='0.75rem' style={{ flex: 'none' }} />
            <small>
                {change === null ? 'Not comparable' : `${change > 0 ? '+' : ''}${change.toFixed(2)}% ${suffix}`}
            </small>
        </span>
    );
};

const CustomRechartsTooltip = ({
    active,
    data = [],
    lineItemLabels,
    payload = [],
    rangeSummary,
    segmentedValue,
    series = [],
    type,
    typeByLineItem,
}: CustomRechartsTooltipProps) => {
    const { token, chartColorPalettes, chartLineItemColors } = useAntDesignTheme();

    if (!active || !payload.length) {
        return null;
    }

    const row = payload[0]?.payload;
    const rowIndex = data.findIndex(
        (candidate) => candidate === row || (candidate.date !== undefined && candidate.date === row?.date),
    );
    const previousRow = rowIndex > 0 ? data[rowIndex - 1] : undefined;
    const intervalLabel =
        segmentedValue === 'annual'
            ? 'YoY'
            : ['quarter', 'quarterly'].includes(segmentedValue ?? '')
              ? 'QoQ'
              : 'period';
    const rangeLabel = rangeSummary
        ? rangeSummary.startLabel === rangeSummary.endLabel
            ? rangeSummary.startLabel
            : `${rangeSummary.startLabel} – ${rangeSummary.endLabel}`
        : undefined;
    const seriesByKey = new Map(series.map((item) => [item.dataKey, item]));
    const groupedPayload = payload.reduce<
        Array<{ company: MetricChartSeries['company']; companyIndex: number; items: TooltipPayloadItem[] }>
    >((groups, item) => {
        const dataKey = String(item.dataKey ?? item.name ?? 'Value');
        const chartSeries = seriesByKey.get(dataKey);
        const company = chartSeries?.company ?? { companyName: 'Company', symbol: 'primary' };
        const companyIndex = chartSeries?.companyIndex ?? 0;
        const existingGroup = groups.find((group) => group.company.symbol === company.symbol);

        if (existingGroup) {
            existingGroup.items.push(item);
        } else {
            groups.push({ company, companyIndex, items: [item] });
        }

        return groups;
    }, []);

    return (
        <Card
            styles={{
                body: {
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left',
                },
            }}
            style={{ borderRadius: token.borderRadius, maxWidth: '24rem', minWidth: '18rem', overflow: 'hidden' }}
        >
            <div
                className='metric-chart-tooltip-scroll'
                style={{ maxHeight: 'min(20rem, 55vh)', overflowY: 'auto', overscrollBehavior: 'contain' }}
            >
                <div
                    className='metric-chart-tooltip-header'
                    style={{
                        background: token.colorBgElevated,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        padding: '0.75rem 1rem 0.625rem',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                    }}
                >
                    <Typography.Title
                        level={5}
                        style={{ color: token.colorTextTertiary, fontWeight: 'initial', margin: 0 }}
                    >
                        {formatPeriodLabel(row, segmentedValue)}
                    </Typography.Title>
                </div>
                <div style={{ padding: '0.625rem 1rem 0.75rem' }}>
                    <Space direction='vertical' size={8} style={{ width: '100%' }}>
                        {groupedPayload.map(({ company, companyIndex, items }, groupIndex) => {
                            const palette = chartColorPalettes[companyIndex % chartColorPalettes.length];

                            return (
                                <div key={company.symbol} style={{ width: '100%' }}>
                                    {groupIndex > 0 ? <Divider style={{ margin: '0.375rem 0 0.5rem' }} /> : null}
                                    <div
                                        style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            gap: '0.375rem',
                                            marginBottom: '0.375rem',
                                        }}
                                    >
                                        <FinancialCompanyAvatar company={company} size={20} />
                                        <Typography.Text strong style={{ fontSize: token.fontSizeSM }}>
                                            {company.companyName}
                                        </Typography.Text>
                                        <TickerExchangeTag
                                            companyName={company.companyName}
                                            exchange={company.exchange}
                                            styles={{ color: token.colorTextSecondary, marginLeft: 0 }}
                                            symbol={company.symbol}
                                            website={company.website}
                                        />
                                    </div>
                                    <Space direction='vertical' size={4} style={{ width: '100%' }}>
                                        {items.map((item) => {
                                            const dataKey = String(item.dataKey ?? item.name ?? 'Value');
                                            const chartSeries = seriesByKey.get(dataKey);
                                            const lineItem = chartSeries?.lineItem ?? dataKey;
                                            const lineItemIndex = chartSeries?.lineItemIndex ?? 0;
                                            const value = numericValue(item.value);
                                            const intervalChange = changePercent(
                                                row?.[dataKey],
                                                previousRow?.[dataKey],
                                            );
                                            const color = palette[lineItemIndex % palette.length];

                                            return (
                                                <div
                                                    className='metric-chart-tooltip-value-row'
                                                    key={dataKey}
                                                    style={{
                                                        alignItems: 'flex-start',
                                                        display: 'flex',
                                                        gap: '0.75rem',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            paddingTop: '0.0625rem',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                alignItems: 'center',
                                                                display: 'inline-flex',
                                                                gap: '0.375rem',
                                                                lineHeight: 1.375,
                                                            }}
                                                        >
                                                            <TbSquareRounded
                                                                fill={color.fill}
                                                                stroke={color.stroke}
                                                                style={{ display: 'block', flex: 'none' }}
                                                            />
                                                            <small>
                                                                {lineItemLabels?.[lineItem] ?? capitalCase(lineItem)}
                                                            </small>
                                                        </span>
                                                    </span>
                                                    <span
                                                        style={{ flex: 'none', marginLeft: 'auto', textAlign: 'right' }}
                                                    >
                                                        <Space
                                                            direction='vertical'
                                                            size={0}
                                                            style={{ alignItems: 'flex-end' }}
                                                        >
                                                            <small
                                                                style={{
                                                                    color: token.colorTextSecondary,
                                                                    lineHeight: 1.375,
                                                                }}
                                                            >
                                                                {value === null ? (
                                                                    'No value'
                                                                ) : (
                                                                    <FinancialMetricValue
                                                                        type={typeByLineItem?.[lineItem] ?? type}
                                                                        value={value}
                                                                    />
                                                                )}
                                                            </small>
                                                            <ChangeValue
                                                                change={intervalChange}
                                                                suffix={intervalLabel}
                                                            />
                                                        </Space>
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </Space>
                                </div>
                            );
                        })}
                    </Space>
                    {rangeSummary && rangeLabel ? (
                        <>
                            <Divider style={{ margin: '0.625rem 0 0.5rem' }} />
                            <Typography.Text style={{ color: token.colorTextTertiary }} type='secondary'>
                                <small>Selected period · {rangeLabel}</small>
                            </Typography.Text>
                            <Space direction='vertical' size={2} style={{ marginTop: '0.25rem', width: '100%' }}>
                                {rangeSummary.items.map((item, index) => {
                                    const color = chartLineItemColors[index % chartLineItemColors.length];

                                    return (
                                        <div
                                            key={item.lineItem}
                                            style={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: '0.75rem',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <span style={{ minWidth: 0 }}>
                                                <span
                                                    style={{
                                                        alignItems: 'center',
                                                        display: 'inline-flex',
                                                        gap: '0.375rem',
                                                    }}
                                                >
                                                    <TbSquareRounded
                                                        fill={color.fill}
                                                        stroke={color.stroke}
                                                        style={{ display: 'block', flex: 'none' }}
                                                    />
                                                    <small>
                                                        {lineItemLabels?.[item.lineItem] ?? capitalCase(item.lineItem)}
                                                    </small>
                                                </span>
                                            </span>
                                            <span style={{ flex: 'none', marginLeft: 'auto', textAlign: 'right' }}>
                                                <ChangeValue change={item.changePercent} suffix='total' />
                                            </span>
                                        </div>
                                    );
                                })}
                            </Space>
                        </>
                    ) : null}
                </div>
            </div>
        </Card>
    );
};

export default CustomRechartsTooltip;
